import { ChevronLeft, ChevronRight, Download, File as FileIcon, Maximize2, RotateCw, ZoomIn, ZoomOut } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { isImageFile, isPdfFile, saveFile } from '@/utils/file-utils'

export interface PreviewFile {
  key: string
  name: string
  /** Pre-signed URL that displays the file. */
  url: string | null
  /** Pre-signed URL that saves the file. */
  attachmentUrl: string | null
  contentType: string | null
}

interface FilePreviewDialogProps {
  files: PreviewFile[]
  index: number
  onIndexChange: (index: number) => void
  open: boolean
  onOpenChange: (open: boolean) => void
}

const ZOOM_STEPS = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4]

/** Viewer for the files of a field: zoom, rotate and step through the files; download from the toolbar. */
export function FilePreviewDialog({ files, index, onIndexChange, open, onOpenChange }: FilePreviewDialogProps) {
  const file = files[index]
  return (
    <Dialog open={open && !!file} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        {file && (
          // Keyed by file so zoom and rotation start over for each one
          <Viewer key={file.key} file={file} index={index} count={files.length} onIndexChange={onIndexChange} />
        )}
      </DialogContent>
    </Dialog>
  )
}

function Viewer({ file, index, count, onIndexChange }: {
  file: PreviewFile
  index: number
  count: number
  onIndexChange: (index: number) => void
}) {
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const isImage = isImageFile(file.contentType, file.name)
  const isPdf = isPdfFile(file.contentType, file.name)

  const zoomIn = () => setZoom((z) => ZOOM_STEPS.find((s) => s > z) ?? z)
  const zoomOut = () => setZoom((z) => [...ZOOM_STEPS].reverse().find((s) => s < z) ?? z)
  const reset = () => { setZoom(1); setRotation(0) }
  const rotate = () => setRotation((r) => (r + 90) % 360)
  const step = (delta: number) => onIndexChange((index + delta + count) % count)
  const download = () => saveFile(file.attachmentUrl, file.url)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return
      if (e.key === 'ArrowLeft' && count > 1) step(-1)
      else if (e.key === 'ArrowRight' && count > 1) step(1)
      else if (!isImage) return
      else if (e.key === '+' || e.key === '=') zoomIn()
      else if (e.key === '-') zoomOut()
      else if (e.key === '0') reset()
      else if (e.key === 'r' || e.key === 'R') rotate()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, count, isImage])

  return (
    <>
      <DialogHeader>
        <DialogTitle className="truncate text-sm">
          {file.name}
          {count > 1 && <span className="ml-2 font-normal text-neutral-400">{index + 1} / {count}</span>}
        </DialogTitle>
      </DialogHeader>

      <div className="relative max-h-[70vh] min-h-[40vh] overflow-auto rounded-lg bg-neutral-50">
        {isImage && file.url ? (
          // shortcut: a rotated image is turned in place, so a very tall one can clip until reset; fine for photos and scans
          <img
            src={file.url}
            alt={file.name}
            draggable={false}
            style={{
              transform: `rotate(${rotation}deg)`,
              ...(zoom === 1 ? {} : { width: `${zoom * 100}%`, maxWidth: 'none' }),
            }}
            className={zoom === 1 ? 'mx-auto max-h-[70vh] max-w-full object-contain transition-transform' : 'mx-auto transition-transform'}
          />
        ) : isPdf && file.url ? (
          <iframe src={file.url} title={file.name} className="h-[70vh] w-full border-0" />
        ) : (
          <div className="flex h-[40vh] flex-col items-center justify-center gap-2 text-sm text-neutral-400">
            <FileIcon className="h-10 w-10 text-neutral-300" />
            No preview for this file type
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          {count > 1 && (
            <>
              <Button type="button" variant="outline" size="sm" className="h-8 px-2" onClick={() => step(-1)} aria-label="Previous file" title="Previous (←)">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button type="button" variant="outline" size="sm" className="h-8 px-2" onClick={() => step(1)} aria-label="Next file" title="Next (→)">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
        <div className="flex items-center gap-1">
          {isImage && (
            <>
              <Button type="button" variant="outline" size="sm" className="h-8 px-2" onClick={zoomOut} disabled={zoom <= ZOOM_STEPS[0]} aria-label="Zoom out" title="Zoom out (−)">
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="w-12 text-center text-xs tabular-nums text-neutral-500" aria-live="polite">{Math.round(zoom * 100)}%</span>
              <Button type="button" variant="outline" size="sm" className="h-8 px-2" onClick={zoomIn} disabled={zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1]} aria-label="Zoom in" title="Zoom in (+)">
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button type="button" variant="outline" size="sm" className="h-8 px-2" onClick={rotate} aria-label="Rotate" title="Rotate (R)">
                <RotateCw className="h-4 w-4" />
              </Button>
              <Button type="button" variant="outline" size="sm" className="h-8 px-2" onClick={reset} disabled={zoom === 1 && rotation === 0} aria-label="Fit to window" title="Fit (0)">
                <Maximize2 className="h-4 w-4" />
              </Button>
            </>
          )}
          <Button type="button" variant="outline" size="sm" className="h-8" onClick={download} disabled={!file.attachmentUrl && !file.url}>
            <Download className="mr-1.5 h-4 w-4" /> Download
          </Button>
        </div>
      </div>
    </>
  )
}
